// THE DOORS A FACT ROW POSTS THROUGH. CLIENT-RECORD-SPEC §5, as ruled on the
// canvas 2026-09-16: every empty required fact is a field in place, and each
// field posts through the ONE existing writer for that fact. This file adds no
// writer; it maps the record's `writer.door` to the client function that
// already exists for that door. A door with no inline sender (roster,
// phf_form) returns null and the row offers the sheet instead.
import { api } from '@/lib/api.js';
import { updateNamedInsured, updateAccountFein } from '@/services/account.service.js';
import { updateAircraftCoverage } from '@/services/deals.service.js';
import { correctPhfField } from '@/services/pilots.service.js';
import { editPersonIdentity, addPersonPhone, relabelPersonPhone, retirePersonPhone, promotePersonPhone, makePerson } from '@/services/people.service.js';

// Address parts post as the named-insured PATCH's sparse keys.
const ADDRESS_KEYS = { street: 'account_street', street_2: 'account_street_2', city: 'account_city', state: 'account_st', zip: 'account_zip' };

// THE PERSON EDIT DOOR's one client path (people-create-door C3): posts a
// sparse body to PATCH /person/:contactId/identity. The name posts as its
// parts ({ first_name, middle_name, last_name }) through this sender; a name is
// never one string split on a guess.
export function personIdentitySender(writer) {
  if (!writer || writer.door !== 'person_identity' || !writer.target?.contact_id) return null;
  return (body) => editPersonIdentity(writer.target.contact_id, body);
}

// THE OTHER-NUMBER DOORS' one client path (people-create-door C6): the same
// person door's Contact, its numbers added (labelled), relabelled, retired (with
// a reason) and made main. Each answers the person read back.
export function personPhonesSender(writer) {
  if (!writer || writer.door !== 'person_identity' || !writer.target?.contact_id) return null;
  const id = writer.target.contact_id;
  return {
    add: ({ phone, label }) => addPersonPhone(id, { phone, label }),
    relabel: (phoneId, label) => relabelPersonPhone(id, phoneId, { label }),
    retire: (phoneId, reason) => retirePersonPhone(id, phoneId, { reason }),
    promote: (phoneId) => promotePersonPhone(id, phoneId),
  };
}

// THE "MAKE A PERSON" DOOR's one client path (people-create-door C8, AR12): the
// record's make_person writer (door fix_pilot), or a roster row's two tokens in
// the same shape. Posts the name (and the optional email and phone) with the
// form token to POST /api/session/:token/fix-pilot; answers the Contact made.
export function makePersonSender(writer) {
  if (!writer || writer.door !== 'fix_pilot' || !writer.target?.session_token || !writer.target?.phf_token) return null;
  return (body) => makePerson(writer.target.session_token, { ...body, phf_token: writer.target.phf_token });
}

export function inlineSender({ writer, accountId, accountName }) {
  if (!writer || !writer.door) return null;
  switch (writer.door) {
    case 'named_insured':
      return async (value) => {
        // The PATCH requires account_name; the record carries it. A value for
        // policyholder_type rides its own key; the address rides its parts.
        // NOTHING ELSE RIDES ALONG: an address save used to carry the record's
        // copy of the policyholder type, and on 2026-09-23 that copy was the
        // stale cached one, so saving a city wrote Corporation back over the
        // Partnership the broker had just chosen. The PATCH reads the current
        // type live and refuses (policyholder_type_required, 1c) when there is
        // none; the address row is gated on the type instead (ClientRecord).
        const body = { account_name: accountName };
        if (writer.key === 'account_name') body.account_name = value;
        else if (writer.key === 'policyholder_type') body.policyholder_type = value;
        else if (writer.key === 'address') for (const [k, v] of Object.entries(value || {})) if (ADDRESS_KEYS[k]) body[ADDRESS_KEYS[k]] = v ?? '';
        return updateNamedInsured(accountId, body);
      };
    case 'account_fein':
      return (value) => updateAccountFein(accountId, value);
    case 'aircraft_coverage':
      if (!writer.target?.deal_id || !writer.target?.tail) return null;
      return (value) => updateAircraftCoverage(writer.target.deal_id, writer.target.tail, { [writer.key]: value });
    case 'aircraft_identity':
      if (!writer.target?.tail) return null;
      return (value) => api.patch(`/api/tracon/aircraft/${encodeURIComponent(writer.target.tail)}/identity`, { [writer.key]: value });
    case 'phf_field':
      if (!writer.target?.id) return null;
      return (value, reason) => correctPhfField(writer.target.id, { field: writer.key, value, reason: reason || 'entered on the client record' });
    case 'person_identity': {
      const send = personIdentitySender(writer);
      if (!send || writer.key === 'name') return null;
      return (value) => send({ [writer.key]: value });
    }
    default:
      return null;
  }
}

// What the row says when it cannot post inline.
export function doorLabel(writer, { hasOpenTerm = false } = {}) {
  if (!writer) return null;
  if (writer.door === 'roster') return 'Edit roster';
  if (writer.door === 'phf_form') return 'or wait for the PHF';
  if (writer.door === 'fix_pilot') return 'not a person yet';
  if (writer.door === 'phf_field' && !writer.target?.id) return 'or wait for the PHF';
  // A coverage fact with no target: either no term is open, or the open term
  // has no canonical row for this tail yet, and the roster sheet seeds one.
  if (writer.door === 'aircraft_coverage' && !writer.target?.deal_id) return hasOpenTerm ? 'add it to the roster first' : 'no open term';
  return null;
}
